import React, { useContext, useEffect, useState } from "react";
import { Tab } from "@headlessui/react";
import { TextInput, Label, Select } from "flowbite-react";
import DataTable from "react-data-table-component";
import GlobalContext from "../connections/GlobalContext";
import engineGif from "../image/engine.gif";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from "chart.js";
ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend);

// ─────────────────────────────────────────────────────────────────────────────
// parseToLocalDate
// Backend now always sends "YYYY-MM-DD".  This helper splits the string
// manually → builds local midnight → zero UTC-shift risk in IST.
// Also handles legacy RFC strings defensively (getUTCFullYear path).
// ─────────────────────────────────────────────────────────────────────────────
const parseToLocalDate = (dateStr) => {
  if (!dateStr) return null;
  const s = String(dateStr).trim();

  // Primary path: ISO "2026-03-25" (what the fixed backend always sends)
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
  }

  // Fallback: RFC "Fri, 06 Mar 2026 00:00:00 GMT" (defensive)
  const d = new Date(s);
  if (!isNaN(d)) {
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }

  return null;
};

const Report = ({ theme }) => {
  const { globalValue } = useContext(GlobalContext);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    purchase: [],
    sales: [],
    service_log: [],
    income: [],
    expenses: [],
    pending_customers: [],
  });
  const [activeTab, setActiveTab] = useState("sales");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [incomeCategories, setIncomeCategories] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [profitLossData, setProfitLossData] = useState([]);
  const [monthlyGraphData, setMonthlyGraphData] = useState({ labels: [], income: [], expense: [] });

  useEffect(() => {
    fetchData();
    fetchIncomeCategories();
    fetchExpenseCategories();
  }, []);

  const fetchData = () => {
    setLoading(true);
    const branch = sessionStorage.getItem("branch");
    fetch(`${globalValue}/report?branch=${encodeURIComponent(branch)}`, {
      method: "GET",
      headers: { "x-access-token": sessionStorage.getItem("token") },
    })
      .then((res) => res.json())
      .then((dat) => {
        const initialData = {
          purchase: [], sales: [], service_log: [],
          income: [], expenses: [], pending_customers: [],
        };
        setData({ ...initialData, ...dat });
      })
      .catch((error) => console.error("Error fetching data:", error))
      .finally(() => setLoading(false));
  };

  const fetchIncomeCategories = () => {
    setLoading(true);
    axios.get(`${globalValue}/category_income_display`)
      .then((res) => setIncomeCategories(res.data))
      .catch((err) => console.error("Error fetching income categories:", err))
      .finally(() => setLoading(false));
  };

  const fetchExpenseCategories = () => {
    setLoading(true);
    axios.get(`${globalValue}/category_display`)
      .then((res) => setExpenseCategories(res.data))
      .catch((err) => console.error("Error fetching expense categories:", err))
      .finally(() => setLoading(false));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = parseToLocalDate(dateStr);
    if (!d) return "N/A";
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // filterByDate — build boundaries by splitting "YYYY-MM-DD" manually so
  // new Date("YYYY-MM-DD") UTC-shift never affects the from/to boundary.
  // ─────────────────────────────────────────────────────────────────────────
  const filterByDate = (items, dateKey) => {
    if (!Array.isArray(items)) return [];

    let from = null;
    let to = null;

    if (fromDate) {
      const [fy, fm, fd] = fromDate.split("-").map(Number);
      from = new Date(fy, fm - 1, fd, 0, 0, 0, 0);
    }
    if (toDate) {
      const [ty, tm, td] = toDate.split("-").map(Number);
      to = new Date(ty, tm - 1, td, 23, 59, 59, 999);
    }

    return items.filter((item) => {
      const itemDate = parseToLocalDate(item[dateKey]);
      if (!itemDate) return false;
      return (!from || itemDate >= from) && (!to || itemDate <= to);
    });
  };

  const getFilteredData = (currentTab) => {
    let itemsToFilter = [];
    let dateKey = "date";

    if (currentTab === "sales")               itemsToFilter = data.sales;
    else if (currentTab === "purchase")       itemsToFilter = data.purchase;
    else if (currentTab === "income")         itemsToFilter = data.income;
    else if (currentTab === "expenses")       itemsToFilter = data.expenses;
    else if (currentTab === "service_log") {  itemsToFilter = data.service_log; dateKey = "s_date"; }
    else if (currentTab === "pending_customers") itemsToFilter = data.pending_customers;

    // Step 1: Date filter (skip for pending_customers)
    let filteredItems =
      currentTab === "pending_customers"
        ? Array.isArray(itemsToFilter) ? [...itemsToFilter] : []
        : filterByDate(itemsToFilter, dateKey);

    // Step 2: Category filter
    if (currentTab === "income" || currentTab === "expenses") {
      if (selectedCategory === "others") {
        filteredItems = filteredItems.filter(
          (item) => !item.category || item.category.trim() === ""
        );
      } else if (selectedCategory) {
        filteredItems = filteredItems.filter(
          (item) => item.category?.toLowerCase() === selectedCategory.toLowerCase()
        );
      }
    }

    // Step 3: Search filter
    if (search.trim()) {
      const keyword = search.toLowerCase();
      if (currentTab === "income") {
        filteredItems = filteredItems.filter((row) =>
          row.category?.toLowerCase().includes(keyword) ||
          String(row.amount || "").includes(keyword) ||
          row.mode?.toLowerCase().includes(keyword) ||
          row.customer_name?.toLowerCase().includes(keyword) ||
          row.vehicle_no?.toLowerCase().includes(keyword) ||
          String(row.bill_no || "").toLowerCase().includes(keyword)
        );
      } else if (currentTab === "expenses") {
        filteredItems = filteredItems.filter((row) =>
          row.category?.toLowerCase().includes(keyword) ||
          String(row.amount || "").includes(keyword) ||
          row.mode?.toLowerCase().includes(keyword)
        );
      } else if (currentTab === "purchase") {
        filteredItems = filteredItems.filter((row) =>
          row.bill_no?.toLowerCase().includes(keyword) ||
          row.vendor_name?.toLowerCase().includes(keyword)
        );
      } else if (currentTab === "sales") {
        filteredItems = filteredItems.filter((row) =>
          row.name?.toLowerCase().includes(keyword) ||
          row.mobile_no?.toLowerCase().includes(keyword)
        );
      } else if (currentTab === "service_log") {
        filteredItems = filteredItems.filter((row) =>
          row.customer_name?.toLowerCase().includes(keyword) ||
          row.mobile_no?.toLowerCase().includes(keyword)
        );
      } else if (currentTab === "pending_customers") {
        filteredItems = filteredItems.filter((row) =>
          row.customer_name?.toLowerCase().includes(keyword) ||
          row.mobile_no?.toLowerCase().includes(keyword) ||
          row.vehicle_no?.toLowerCase().includes(keyword) ||
          row.brand?.toLowerCase().includes(keyword) ||
          row.model?.toLowerCase().includes(keyword) ||
          row.address?.toLowerCase().includes(keyword)
        );
      }
    }

    return filteredItems;
  };

  const formatNumber = (num) =>
    Number(num).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const getTotalAmount = (currentTab, filteredData) => {
    if (!Array.isArray(filteredData)) return 0;
    switch (currentTab) {
      case "sales":    return filteredData.reduce((s, i) => s + (Number(i.grand_total) || 0), 0);
      case "purchase": return filteredData.reduce((s, i) => s + (Number(i.total_amount) || 0), 0);
      case "income":
      case "expenses": return filteredData.reduce((s, i) => s + (Number(i.amount) || 0), 0);
      default:         return 0;
    }
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    const title = "JALS AUTOMOBILES - " + activeTab.charAt(0).toUpperCase() + activeTab.slice(1) + " Report";
    doc.text(title, 14, 10);

    let startY = 15;
    if (fromDate || toDate) {
      doc.setFontSize(10);
      const fromText = fromDate ? `From: ${formatDate(fromDate)}` : "";
      const toText = toDate ? `To: ${formatDate(toDate)}` : "";
      doc.text(`${fromText}   ${toText}`, 14, 16);
      startY = 25;
    }

    let rows = [];
    let columns = [];
    const tableData = getFilteredData(activeTab);

    if (activeTab === "sales") {
      columns = ["Date", "Name", "Mobile", "Address", "Total"];
      rows = tableData.map((row) => [formatDate(row.date), row.name, row.mobile_no, row.address, row.grand_total]);
    } else if (activeTab === "purchase") {
      columns = ["Date", "Bill No", "Vendor", "Total", "Paid", "Mode"];
      rows = tableData.map((row) => [formatDate(row.date), row.bill_no, row.vendor_name, row.total_amount, row.amount_paid, row.pay_mode]);
    } else if (activeTab === "income") {
      columns = ["Date", "Invoice No", "Customer Name", "Vehicle No", "Category", "Amount", "Mode"];
      rows = tableData.map((row) => [formatDate(row.date), row.bill_no || "-", row.customer_name || "-", row.vehicle_no || "-", row.category, Number(row.amount || 0).toFixed(2), row.mode]);
    } else if (activeTab === "expenses") {
      columns = ["Date", "Category", "Amount", "Staff/Expense"];
      rows = tableData.map((row) => [formatDate(row.date), row.category, Number(row.amount || 0).toFixed(2), row.mode]);
    } else if (activeTab === "service_log") {
      columns = ["Start Date", "Estimate ID", "Customer", "End Date", "Mode"];
      rows = tableData.map((row) => [formatDate(row.s_date), row.estimate_id, row.customer_name, formatDate(row.e_date), row.mode]);
    } else if (activeTab === "pending_customers") {
      columns = ["Customer Name", "Mobile No", "Brand", "Model", "Vehicle No", "Address"];
      rows = tableData.map((row) => [row.customer_name, row.mobile_no, row.brand, row.model, row.vehicle_no, row.address]);
    } else if (activeTab === "profit_loss") {
      columns = ["Month", "Total Income", "Total Expense", "Profit / Loss"];
      rows = profitLossData.map((row) => [row.month, formatNumber(row.income), formatNumber(row.expense), formatNumber(row.profit)]);
    }

    if (rows.length > 0 && activeTab !== "service_log" && activeTab !== "pending_customers") {
      let sumColumnIndex = null;
      const findIndex = activeTab === "purchase" ? 3 : 1;
      for (let i = rows[0].length - findIndex; i >= 0; i--) {
        if (rows.some((r) => !isNaN(parseFloat(r[i])))) { sumColumnIndex = i; break; }
      }
      if (sumColumnIndex !== null) {
        const totalValue = rows.reduce((sum, r) => { const v = parseFloat(r[sumColumnIndex]); return sum + (isNaN(v) ? 0 : v); }, 0);
        const totalRow = Array(rows[0].length).fill("");
        totalRow[sumColumnIndex - 1] = "TOTAL";
        totalRow[sumColumnIndex] = totalValue.toFixed(2);
        rows.push(totalRow);
      }
    }

    autoTable(doc, {
      head: [columns], body: rows, startY, styles: { fontSize: 8 },
      didParseCell: (data) => {
        if (data.row.index === rows.length - 1 && activeTab !== "service_log" && activeTab !== "pending_customers") {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [220, 220, 220];
        }
      },
    });
    doc.save(`${activeTab}_report.pdf`);
  };

  const customStyles = {
    table:      { style: { backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white" } },
    headCells:  { style: { backgroundColor: theme === "dark" ? "rgb(55, 65, 81)" : "rgb(226, 232, 240)", color: theme === "dark" ? "white" : "black", fontWeight: "bold" } },
    cells:      { style: { color: theme === "dark" ? "white" : "black", backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white" } },
    pagination: { style: { color: theme === "dark" ? "white" : "black", backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white" } },
  };

  // ─────────────────────────────────────────────────────────────────────────
  // keyField: tell DataTable to use _row_key (guaranteed unique by backend)
  // instead of defaulting to "id" (which is shared across advance records,
  // income payment rows with the same income.id, etc.).
  // This was the final cause of ghost/duplicate rendered rows in the table.
  // ─────────────────────────────────────────────────────────────────────────
  const KEY_FIELD = "_row_key";

  const salesColumns = [
    { name: "Date",   selector: (r) => formatDate(r.date), sortable: true, width: "120px" },
    { name: "Name",   selector: (r) => r.name,       sortable: true },
    { name: "Mobile", selector: (r) => r.mobile_no },
    { name: "Address",selector: (r) => r.address },
    { name: "Total",  sortable: true, selector: (r) => Number(r.grand_total), cell: (r) => Number(r.grand_total).toFixed(2) },
  ];

  const purchaseColumns = [
    { name: "Date",    selector: (r) => formatDate(r.date), sortable: true, width: "120px" },
    { name: "Bill No", selector: (r) => r.bill_no,      sortable: true },
    { name: "Vendor",  selector: (r) => r.vendor_name,  sortable: true },
    { name: "Total",   sortable: true, selector: (r) => Number(r.total_amount || 0), cell: (r) => Number(r.total_amount || 0).toFixed(2) },
    { name: "Mode",    selector: (r) => r.pay_mode },
  ];

  const incomeColumns = [
    { name: "Date",          selector: (r) => formatDate(r.date), sortable: true, width: "120px" },
    { name: "Invoice No",    selector: (r) => r.bill_no || "-",   sortable: true },
    { name: "Customer Name", selector: (r) => r.customer_name || "-", sortable: true },
    { name: "Vehicle No",    selector: (r) => r.vehicle_no || "-",    sortable: true },
    { name: "Category",      selector: (r) => r.category,         sortable: true },
    { name: "Amount",        sortable: true, selector: (r) => Number(r.amount || 0), cell: (r) => Number(r.amount || 0).toFixed(2) },
    { name: "Mode",          selector: (r) => r.mode,             sortable: true },
  ];

  const expenseColumns = [
    { name: "Date",         selector: (r) => formatDate(r.date), sortable: true, width: "120px" },
    { name: "Category",     selector: (r) => r.category,         sortable: true },
    { name: "Amount",       sortable: true, selector: (r) => Number(r.amount || 0), cell: (r) => Number(r.amount || 0).toFixed(2) },
    { name: "Staff/Expense",selector: (r) => r.mode,             sortable: true },
  ];

  const serviceColumns = [
    { name: "Start Date",  selector: (r) => formatDate(r.s_date), sortable: true, width: "120px" },
    { name: "Estimate ID", selector: (r) => r.estimate_id },
    { name: "Customer",    selector: (r) => r.customer_name, sortable: true },
    { name: "Mobile No",   selector: (r) => r.mobile_no },
    { name: "End Date",    selector: (r) => formatDate(r.e_date), sortable: true },
    { name: "Mode",        selector: (r) => r.mode },
  ];

  const pendingCustomerColumns = [
    { name: "Customer Name", selector: (r) => r.customer_name, sortable: true },
    { name: "Mobile No",     selector: (r) => r.mobile_no },
    { name: "Brand",         selector: (r) => r.brand },
    { name: "Model",         selector: (r) => r.model },
    { name: "Vehicle No",    selector: (r) => r.vehicle_no },
    { name: "Address",       selector: (r) => r.address },
  ];

  const profitLossColumns = [
    { name: "Month",        selector: (r) => r.month,           sortable: true },
    { name: "Total Income", sortable: true, selector: (r) => Number(r.income),  cell: (r) => formatNumber(r.income) },
    { name: "Total Expense",sortable: true, selector: (r) => Number(r.expense), cell: (r) => formatNumber(r.expense) },
    {
      name: "Profit / Loss", sortable: true, selector: (r) => Number(r.profit),
      cell: (r) => (
        <span className={r.profit >= 0 ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>
          {formatNumber(r.profit)}
        </span>
      ),
    },
  ];

  const tabs = ["sales", "purchase", "income", "expenses", "service_log", "pending_customers", "profit_loss"];

  const getMonthlySummary = (items, dateKey) => {
    if (!Array.isArray(items)) return {};
    const monthly = {};
    items.forEach((item) => {
      const d = parseToLocalDate(item[dateKey]);
      if (d && !isNaN(d)) {
        const key = `${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
        if (!monthly[key]) monthly[key] = 0;
        monthly[key] += Number(item.amount ?? item.total_amount ?? item.grand_total ?? 0);
      }
    });
    return monthly;
  };

  useEffect(() => {
    const incomeFiltered  = filterByDate(data.income,   "date");
    const expenseFiltered = filterByDate(data.expenses, "date");

    const incomeMonthly  = getMonthlySummary(incomeFiltered,  "date");
    const expenseMonthly = getMonthlySummary(expenseFiltered, "date");

    const labels = Array.from(
      new Set([...Object.keys(incomeMonthly), ...Object.keys(expenseMonthly)])
    ).sort((a, b) => {
      const [am, ay] = a.split("/"); const [bm, by] = b.split("/");
      return new Date(`${ay}-${am}-01`) - new Date(`${by}-${bm}-01`);
    });

    const plTbl = labels.map((label) => {
      const inc = incomeMonthly[label]  || 0;
      const exp = expenseMonthly[label] || 0;
      return { month: label, income: inc, expense: exp, profit: inc - exp };
    });

    if (plTbl.length) {
      plTbl.push({
        month: "Total",
        income:  plTbl.reduce((a, b) => a + b.income,  0),
        expense: plTbl.reduce((a, b) => a + b.expense, 0),
        profit:  plTbl.reduce((a, b) => a + b.income - b.expense, 0),
      });
    }

    setProfitLossData(plTbl);
    setMonthlyGraphData({
      labels,
      income:  labels.map((l) => incomeMonthly[l]  || 0),
      expense: labels.map((l) => expenseMonthly[l] || 0),
    });
  }, [fromDate, toDate, data]);

  const lineData = {
    labels: monthlyGraphData.labels,
    datasets: [
      { label: "Income",   data: monthlyGraphData.income,  borderColor: "#7c3aed", backgroundColor: "rgba(124,58,237,0.08)", tension: 0.3, fill: false },
      { label: "Expenses", data: monthlyGraphData.expense, borderColor: "#f87171", backgroundColor: "rgba(248,113,113,0.08)", tension: 0.3, fill: false },
    ],
  };

  const lineOptions = {
    plugins: { legend: { labels: { color: theme === "dark" ? "white" : "#222" } } },
    scales: {
      x: { ticks: { color: theme === "dark" ? "white" : "#222" } },
      y: { ticks: { color: theme === "dark" ? "white" : "#222" } },
    },
  };

  return (
    <div>
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-200 bg-opacity-50 z-50">
          <img src={engineGif} alt="Loading..." className="w-24 h-24" />
        </div>
      )}
      <div className={`md:ml-[250px] w-full md:w-[calc(100%-250px)] mt-16 h-[92vh] flex justify-center items-center box-border ${theme === "dark" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-800"}`}>
        <div className={`w-[95%] md:w-[98%] h-[95%] px-4 py-2 rounded-xl shadow-xl overflow-auto ${theme === "dark" ? "bg-gray-800 text-gray-200" : "bg-white text-gray-800"}`}>

          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Report Summary</h2>
            <button onClick={handleExportPDF} className="px-4 py-2 text-white bg-green-600 hover:bg-green-700 rounded">
              Export PDF
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-4">
            <div className="flex flex-col">
              <Label htmlFor="fromDate" value="From Date" className="mb-1 text-sm" />
              <input id="fromDate" type="date" value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                disabled={activeTab === "pending_customers"}
                className={`border rounded px-2 py-1 ${theme === "dark" ? "bg-gray-700 border-gray-600" : "bg-white border-gray-300"}`}
              />
            </div>
            <div className="flex flex-col">
              <Label htmlFor="toDate" value="To Date" className="mb-1 text-sm" />
              <input id="toDate" type="date" value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                disabled={activeTab === "pending_customers"}
                className={`border rounded px-2 py-1 ${theme === "dark" ? "bg-gray-700 border-gray-600" : "bg-white border-gray-300"}`}
              />
            </div>

            {activeTab === "income" && (
              <div className="flex gap-4 flex-wrap flex-1 min-w-[200px]">
                <div className="flex flex-col">
                  <Label htmlFor="categoryFilter" value="Filter by Category" className="mb-1 text-sm" />
                  <Select id="categoryFilter" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}
                    className={theme === "dark" ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"}>
                    <option value="">All Categories</option>
                    {incomeCategories.map((cat) => <option key={cat.id} value={cat.category_name}>{cat.category_name}</option>)}
                    <option value="others">Others</option>
                  </Select>
                </div>
                <div className="flex flex-col">
                  <Label htmlFor="search" value="Search" className="mb-1 text-sm" />
                  <TextInput id="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Invoice, customer, vehicle, amount..." />
                </div>
              </div>
            )}

            {activeTab === "expenses" && (
              <div className="flex gap-4 flex-wrap flex-1 min-w-[200px]">
                <div className="flex flex-col">
                  <Label htmlFor="categoryFilter" value="Filter by Category" className="mb-1 text-sm" />
                  <Select id="categoryFilter" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}
                    className={theme === "dark" ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"}>
                    <option value="">All Categories</option>
                    {expenseCategories.map((cat) => <option key={cat.id} value={cat.category_name}>{cat.category_name}</option>)}
                    <option value="others">Others</option>
                  </Select>
                </div>
                <div className="flex flex-col">
                  <Label htmlFor="search" value="Search" className="mb-1 text-sm" />
                  <TextInput id="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Category, amount, mode..." />
                </div>
              </div>
            )}

            {activeTab !== "income" && activeTab !== "expenses" && activeTab !== "profit_loss" && (
              <div className="flex flex-col flex-1 min-w-[200px]">
                <Label htmlFor="search" value="Search" className="mb-1 text-sm" />
                <TextInput id="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." />
              </div>
            )}
          </div>

          {/* Tabs */}
          <Tab.Group selectedIndex={tabs.indexOf(activeTab)} onChange={(i) => { setActiveTab(tabs[i]); setSearch(""); setSelectedCategory(""); }}>
            <Tab.List className="flex justify-between w-full mb-4 border-b bg-gray-100 rounded-t-lg overflow-hidden">
              {tabs.map((tab) => (
                <Tab key={tab} className={({ selected }) =>
                  `flex-1 text-center px-6 py-3 transition-all duration-200 text-sm md:text-base ${selected ? "bg-blue-600 text-white font-semibold" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`
                }>
                  {tab.replace(/_/g, " ").split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}
                </Tab>
              ))}
            </Tab.List>

            <Tab.Panels>
              {/* Sales */}
              <Tab.Panel>
                <DataTable columns={salesColumns} data={getFilteredData("sales")}
                  keyField="id" customStyles={customStyles} pagination striped highlightOnHover />
                <div className="mt-2 text-right font-semibold text-lg">
                  Total: ₹ {formatNumber(getTotalAmount("sales", getFilteredData("sales")))}
                </div>
              </Tab.Panel>

              {/* Purchase */}
              <Tab.Panel>
                <DataTable columns={purchaseColumns} data={getFilteredData("purchase")}
                  keyField="id" customStyles={customStyles} pagination striped highlightOnHover />
                <div className="mt-2 text-right font-semibold text-lg">
                  Total: ₹ {formatNumber(getTotalAmount("purchase", getFilteredData("purchase")))}
                </div>
              </Tab.Panel>

              {/* Income — uses _row_key to prevent ghost rows */}
              <Tab.Panel>
                <DataTable columns={incomeColumns} data={getFilteredData("income")}
                  keyField={KEY_FIELD} customStyles={customStyles} pagination striped highlightOnHover />
                <div className="mt-2 text-right font-semibold text-lg">
                  Total: ₹ {formatNumber(getTotalAmount("income", getFilteredData("income")))}
                </div>
              </Tab.Panel>

              {/* Expenses — uses _row_key */}
              <Tab.Panel>
                <DataTable columns={expenseColumns} data={getFilteredData("expenses")}
                  keyField={KEY_FIELD} customStyles={customStyles} pagination striped highlightOnHover />
                <div className="mt-2 text-right font-semibold text-lg">
                  Total: ₹ {formatNumber(getTotalAmount("expenses", getFilteredData("expenses")))}
                </div>
              </Tab.Panel>

              {/* Service Log */}
              <Tab.Panel>
                <DataTable columns={serviceColumns} data={getFilteredData("service_log")}
                  keyField="id" customStyles={customStyles} pagination striped highlightOnHover />
              </Tab.Panel>

              {/* Pending Customers */}
              <Tab.Panel>
                <DataTable columns={pendingCustomerColumns} data={getFilteredData("pending_customers")}
                  keyField="customer_id" customStyles={customStyles} pagination striped highlightOnHover />
              </Tab.Panel>

              {/* Profit & Loss */}
              <Tab.Panel>
                <div className="mb-8">
                  <div className="flex flex-col md:flex-row flex-wrap gap-8 justify-center items-center mb-8">
                    <div className="bg-white dark:bg-gray-900 shadow-lg rounded-xl p-8 flex-1 min-w-[230px] text-center border border-purple-200">
                      <h3 className="text-2xl font-semibold text-purple-800 mb-3">Total Income</h3>
                      <div className="text-3xl font-bold text-green-600">
                        ₹ {formatNumber(profitLossData.length ? profitLossData[profitLossData.length - 1].income : 0)}
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-900 shadow-lg rounded-xl p-8 flex-1 min-w-[230px] text-center border border-purple-200">
                      <h3 className="text-2xl font-semibold text-purple-800 mb-3">Total Expense</h3>
                      <div className="text-3xl font-bold text-red-600">
                        ₹ {formatNumber(profitLossData.length ? profitLossData[profitLossData.length - 1].expense : 0)}
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-900 shadow-lg rounded-xl p-8 flex-1 min-w-[230px] text-center border border-green-300">
                      <h3 className="text-2xl font-semibold text-green-700 mb-3">Profit</h3>
                      <div className="text-3xl font-bold text-green-600">
                        ₹ {formatNumber(profitLossData.length && profitLossData[profitLossData.length - 1].profit > 0 ? profitLossData[profitLossData.length - 1].profit : 0)}
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-900 shadow-lg rounded-xl p-8 flex-1 min-w-[230px] text-center border border-red-300">
                      <h3 className="text-2xl font-semibold text-red-700 mb-3">Loss</h3>
                      <div className="text-3xl font-bold text-red-600">
                        ₹ {formatNumber(profitLossData.length && profitLossData[profitLossData.length - 1].profit < 0 ? Math.abs(profitLossData[profitLossData.length - 1].profit) : 0)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 w-full max-w-3xl mx-auto">
                    <h3 className="text-lg font-semibold mb-3 text-purple-800 text-center">Monthly Income & Expense Trend</h3>
                    <Line data={lineData} options={lineOptions} height={110} />
                  </div>

                  <div className="mt-8">
                    <DataTable columns={profitLossColumns} data={profitLossData}
                      keyField="month" customStyles={customStyles} striped highlightOnHover pagination />
                  </div>
                </div>
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </div>
      </div>
    </div>
  );
};

export default Report;